// Batch payouts slice.
//
// `status` is a required query param on the list endpoint, so batches are stored
// per status — there is no combined list to keep, and pretending otherwise would
// mean merging pages that were never comparable.
//
// The two settlement operations carry `submitting`, which is the double-click
// guard. It is set on REQUEST and cleared only on SUCCESS or FAILURE, and the
// button reads it. Server state remains the final truth — this only prevents a
// second dispatch.

import * as types from '../../lib/actionTypes.es6';
import {
  createListReducer,
  createWriteReducer,
  initialListState,
  initialWriteState,
  readListResponse
} from '../../components/CommissionsShared/listReducerFactory.es6';

// The statuses a batch payout can be listed under. Driven by the shared
// transaction status enum, narrowed to those meaningful for a batch.
export var BATCH_STATUS_TABS = [
  'Pending',
  'In_Progress',
  'Completed',
  'Paid',
  'Failed',
  'Failed_Payment',
  'Failed_To_Update_Account',
  'Cancelled'
];

var listReducer = createListReducer(
  {
    request: types.GET_BATCH_PAYOUTS_REQUEST,
    success: types.GET_BATCH_PAYOUTS_SUCCESS,
    failure: types.GET_BATCH_PAYOUTS_FAILURE,
    detailSuccess: types.GET_BATCH_PAYOUT_SUCCESS
  },
  { keepRowsWhileLoading: true }
);

var WRITES = {
  create: {
    request: types.CREATE_BATCH_PAYOUT_REQUEST,
    success: types.CREATE_BATCH_PAYOUT_SUCCESS,
    failure: types.CREATE_BATCH_PAYOUT_FAILURE,
    reset: types.RESET_CREATE_BATCH_PAYOUT_SUCCESS
  },
  pay: {
    request: types.PAY_BATCH_PAYOUTS_REQUEST,
    success: types.PAY_BATCH_PAYOUTS_SUCCESS,
    failure: types.PAY_BATCH_PAYOUTS_FAILURE,
    reset: types.RESET_PAY_BATCH_PAYOUTS_SUCCESS
  },
  confirm: {
    request: types.CONFIRM_BATCH_PAYOUT_REQUEST,
    success: types.CONFIRM_BATCH_PAYOUT_SUCCESS,
    failure: types.CONFIRM_BATCH_PAYOUT_FAILURE,
    reset: types.RESET_CONFIRM_BATCH_PAYOUT_SUCCESS
  }
};

var ERROR_RESETS = {
  create: types.RESET_CREATE_BATCH_PAYOUT_ERROR,
  pay: types.RESET_PAY_BATCH_PAYOUTS_ERROR,
  confirm: types.RESET_CONFIRM_BATCH_PAYOUT_ERROR
};

var writeReducers = {};
Object.keys(WRITES).forEach(function(name) {
  writeReducers[name] = createWriteReducer(WRITES[name]);
});

export function initialState() {
  var state = {
    // status -> list state. Only the statuses actually visited are populated.
    byStatus: {},
    activeStatus: BATCH_STATUS_TABS[0],
    listParams: {},

    detailLoading: false,
    detailError: null,
    // Batches merge into here from both the list and the detail fetch.
    byGuid: {},

    polling: false,
    pollingGuid: null,

    // agency guid -> { guids, loading, error }: the agents of each agency the
    // builder has been asked to pay.
    agencyAgents: {}
  };
  Object.keys(writeReducers).forEach(function(name) {
    state[name] = initialWriteState();
  });
  return state;
}

function agencyAgentsReducer(state, action) {
  var agencyGuid = action.meta
    ? action.meta.agencyGuid
    : action.payload && action.payload.agencyGuid;
  if (!agencyGuid) return state;
  var existing = state.agencyAgents[agencyGuid] || {
    guids: [],
    loading: false,
    error: null
  };
  var patch;
  switch (action.type) {
    case types.GET_AGENCY_AGENTS_REQUEST:
      patch = { loading: true, error: null };
      break;
    case types.GET_AGENCY_AGENTS_SUCCESS:
      patch = {
        loading: false,
        error: null,
        guids: readListResponse(action.payload).rows.map(function(agent) {
          return agent.guid;
        })
      };
      break;
    case types.GET_AGENCY_AGENTS_FAILURE:
      patch = {
        loading: false,
        error: action.payload || { message: 'Request failed' }
      };
      break;
    default:
      return state;
  }
  var updated = {};
  updated[agencyGuid] = Object.assign({}, existing, patch);
  return Object.assign({}, state, {
    agencyAgents: Object.assign({}, state.agencyAgents, updated)
  });
}

function statusOf(action) {
  return (action.payload && action.payload.status) || null;
}

export default function batchPayoutsReducer(state, action) {
  if (state === undefined) state = initialState();
  if (!action || !action.type) return state;

  var next = agencyAgentsReducer(state, action);

  // Route list actions to the sub-list for the status they concern.
  var LIST_TYPES = [
    types.GET_BATCH_PAYOUTS_REQUEST,
    types.GET_BATCH_PAYOUTS_SUCCESS,
    types.GET_BATCH_PAYOUTS_FAILURE
  ];
  if (LIST_TYPES.indexOf(action.type) !== -1) {
    // The status comes from the request payload; success and failure carry it
    // through via `meta` so the response lands in the right bucket.
    var status =
      statusOf(action) ||
      (action.meta && action.meta.status) ||
      state.activeStatus;

    var before = state.byStatus[status] || initialListState();
    var after = listReducer(before, action);
    var bucket = {};
    bucket[status] = after;

    next = Object.assign({}, next, {
      byStatus: Object.assign({}, state.byStatus, bucket),
      activeStatus: status
    });

    if (action.type === types.GET_BATCH_PAYOUTS_REQUEST) {
      next = Object.assign({}, next, { listParams: action.payload || {} });
    }
    // Keep the shared batch cache in step with whatever the page returned.
    if (action.type === types.GET_BATCH_PAYOUTS_SUCCESS) {
      next = Object.assign({}, next, {
        byGuid: Object.assign({}, next.byGuid, after.byGuid)
      });
    }
  }

  Object.keys(writeReducers).forEach(function(name) {
    var previous = next[name];
    var updated = writeReducers[name](previous, action);
    if (updated !== previous) {
      var patch = {};
      patch[name] = updated;
      next = Object.assign({}, next, patch);
    }
  });

  switch (action.type) {
    case types.GET_BATCH_PAYOUT_REQUEST:
      return Object.assign({}, next, {
        detailLoading: true,
        detailError: null
      });

    case types.GET_BATCH_PAYOUT_SUCCESS: {
      var batch = action.payload;
      var merged = next.byGuid;
      if (batch && batch.guid) {
        var one = {};
        one[batch.guid] = batch;
        merged = Object.assign({}, next.byGuid, one);
      }
      return Object.assign({}, next, {
        detailLoading: false,
        detailError: null,
        byGuid: merged
      });
    }

    case types.GET_BATCH_PAYOUT_FAILURE:
      return Object.assign({}, next, {
        detailLoading: false,
        detailError: action.payload || { message: 'Could not load this batch' }
      });

    case types.START_BATCH_PAYOUT_POLLING:
      return Object.assign({}, next, {
        polling: true,
        pollingGuid: (action.payload && action.payload.guid) || null
      });

    case types.STOP_BATCH_PAYOUT_POLLING:
      return Object.assign({}, next, { polling: false, pollingGuid: null });

    default:
      break;
  }

  var names = Object.keys(ERROR_RESETS);
  for (var i = 0; i < names.length; i += 1) {
    if (action.type === ERROR_RESETS[names[i]]) {
      var cleared = {};
      cleared[names[i]] = Object.assign({}, next[names[i]], { error: null });
      return Object.assign({}, next, cleared);
    }
  }

  return next;
}
